// src/utils/margem.ts
//
// Fórmula única da margem do produto na oportunidade (espelha o backend:
// ProdutoOportunidade::calcularMargemLucro).
//
// Base de cálculo:
//   preço de venda = valor de REFERÊNCIA (sempre a base)
//   custo          = valor de PROPOSTA, quando preenchido (prevalece);
//                    sem proposta, usa o valor ORÇADO (menor orçamento).
//                    Proposta e orçado ocupam o mesmo lugar na conta.
//
//   resultado = referência - custo - custo logístico - custos adicionais - (referência x imposto%)
//   margem %  = resultado / referência x 100

export interface EntradaMargem {
    valorProposta?: number;
    valorReferencia: number;
    valorOrcado: number;
    custoLogistico?: number;
    custosAdicionais?: number;
    percentualImposto?: number;
}

export interface ResultadoMargem {
    // Falso quando faltam referência ou custo (proposta/orçado): não há o que comparar
    calculavel: boolean;
    resultado: number;
    margemPorcentagem: number;
    // Qual valor serviu de custo na conta
    baseUsada: 'proposta' | 'orcado';
}

export type NivelMargem = 'baixa' | 'mediana' | 'boa';

const paraNumero = (valor: unknown): number => {
    const n = typeof valor === 'string' ? parseFloat(valor) : Number(valor);
    return isNaN(n) ? 0 : n;
};

export const calcularMargem = (entrada: EntradaMargem): ResultadoMargem => {
    const proposta = paraNumero(entrada.valorProposta);
    const referencia = paraNumero(entrada.valorReferencia);
    const orcado = paraNumero(entrada.valorOrcado);

    // A proposta prevalece sobre o orçado: os dois são o "custo" frente à referência
    const baseUsada: 'proposta' | 'orcado' = proposta > 0 ? 'proposta' : 'orcado';
    const custo = baseUsada === 'proposta' ? proposta : orcado;

    if (referencia <= 0 || custo <= 0) {
        return { calculavel: false, resultado: 0, margemPorcentagem: 0, baseUsada };
    }

    const logistico = paraNumero(entrada.custoLogistico);
    const adicionais = paraNumero(entrada.custosAdicionais);
    const imposto = paraNumero(entrada.percentualImposto);

    const valorImposto = referencia * (imposto / 100);
    const resultado = referencia - custo - logistico - adicionais - valorImposto;
    const margemPorcentagem = (resultado / referencia) * 100;

    return { calculavel: true, resultado, margemPorcentagem, baseUsada };
};

// Faixas atuais de cor (mesmas do restante do sistema)
export const nivelDaMargem = (margemPorcentagem: number): NivelMargem => {
    if (margemPorcentagem <= 8) return 'baixa';
    if (margemPorcentagem < 15) return 'mediana';
    return 'boa';
};
